import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiPlus, HiTrash, HiXMark, HiCurrencyRupee } from 'react-icons/hi2';
import { getInvoices, createInvoice, deleteInvoice, recordPayment } from '../../services/api';

const STATUS_CLASS = {
  unpaid: 'badge-red', partial: 'badge-yellow', paid: 'badge-green',
  overdue: 'badge-red', cancelled: 'badge-gray',
};
const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

export default function Billing() {
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [newInvoiceModal, setNewInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ customer_id: '', amount: '', due_date: '', notes: '' });
  const [savingInvoice, setSavingInvoice] = useState(false);

  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', transaction_ref: '', notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    const params = { page, limit, status: statusFilter || undefined, from: dateFrom || undefined, to: dateTo || undefined };
    getInvoices(params)
      .then((res) => {
        const data = res.data;
        setInvoices(Array.isArray(data) ? data : data.invoices || []);
        setTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => toast.error('Failed to load invoices.'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Summary calculations
  const totalInvoiced = invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const totalCollected = invoices.reduce((s, i) => s + (Number(i.paid_amount) || 0), 0);
  const totalDue = invoices.reduce((s, i) => s + (Number(i.balance) || Number(i.amount) - Number(i.paid_amount) || 0), 0);

  // Payments from invoices (paid ones for the payments tab)
  const payments = invoices.filter((i) => Number(i.paid_amount) > 0);

  const handleInvoiceFormChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm((f) => ({ ...f, [name]: value }));
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceForm.customer_id || !invoiceForm.amount) { toast.error('Customer ID and amount are required.'); return; }
    setSavingInvoice(true);
    try {
      await createInvoice({
        customer_id: parseInt(invoiceForm.customer_id),
        amount: Number(invoiceForm.amount),
        due_date: invoiceForm.due_date || undefined,
        notes: invoiceForm.notes || undefined,
      });
      toast.success('Invoice created.');
      setNewInvoiceModal(false);
      setInvoiceForm({ customer_id: '', amount: '', due_date: '', notes: '' });
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create invoice.');
    } finally {
      setSavingInvoice(false);
    }
  };

  const openPaymentModal = (invoice) => {
    setPaymentModal(invoice);
    setPaymentForm({ amount: '', method: 'cash', transaction_ref: '', notes: '' });
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((f) => ({ ...f, [name]: value }));
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) { toast.error('Enter a valid payment amount.'); return; }
    setSavingPayment(true);
    try {
      await recordPayment(paymentModal.id || paymentModal._id, {
        amount: Number(paymentForm.amount),
        method: paymentForm.method,
        transaction_ref: paymentForm.transaction_ref || undefined,
        notes: paymentForm.notes || undefined,
      });
      toast.success('Payment recorded.');
      setPaymentModal(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to record payment.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteInvoice(deleteTarget.id || deleteTarget._id);
      toast.success('Invoice deleted.');
      setDeleteTarget(null);
      fetchInvoices();
    } catch {
      toast.error('Failed to delete invoice.');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Billing</h1>
        <button className="btn btn-primary" onClick={() => setNewInvoiceModal(true)}><HiPlus /> New Invoice</button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-3" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="stat-label">Total Invoiced</div>
          <div className="stat-value" style={{ color: '#0ea5e9' }}>Rs.{fmt(totalInvoiced)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Collected</div>
          <div className="stat-value" style={{ color: '#22c55e' }}>Rs.{fmt(totalCollected)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Due</div>
          <div className="stat-value" style={{ color: '#ef4444' }}>Rs.{fmt(totalDue)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '2px solid var(--border)' }}>
        {['invoices', 'payments'].map((tab) => (
          <button
            key={tab}
            className="btn btn-secondary"
            style={{
              borderRadius: '0.375rem 0.375rem 0 0',
              borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--blue)' : undefined,
              fontWeight: activeTab === tab ? 600 : 400,
              marginBottom: '-2px',
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'invoices' && (
        <>
          {/* Filters */}
          <div className="filter-bar">
            <select className="form-control" style={{ width: '150px' }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {['unpaid', 'partial', 'paid', 'overdue', 'cancelled'].map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>From:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>To:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            </div>
            {(statusFilter || dateFrom || dateTo) && (
              <button className="btn btn-secondary btn-sm" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
                <HiXMark /> Clear
              </button>
            )}
          </div>

          <div className="card">
            {loading ? (
              <div className="loading-center"><div className="spinner" /></div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Customer</th>
                      <th>Amount Rs.</th>
                      <th>Paid Rs.</th>
                      <th>Balance Rs.</th>
                      <th>Status</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8' }}>No invoices found.</td></tr>
                    ) : (
                      invoices.map((inv) => {
                        const balance = Number(inv.balance) || Number(inv.amount) - Number(inv.paid_amount || 0);
                        return (
                          <tr key={inv.id || inv._id}>
                            <td style={{ fontWeight: 600 }}>{inv.invoice_number || `#${inv.id || inv._id}`}</td>
                            <td>{inv.customer_name || inv.customer?.name || `ID: ${inv.customer_id}`}</td>
                            <td>Rs.{fmt(inv.amount)}</td>
                            <td>Rs.{fmt(inv.paid_amount || 0)}</td>
                            <td>Rs.{fmt(balance)}</td>
                            <td><span className={`badge ${STATUS_CLASS[inv.status] || 'badge-gray'}`}>{inv.status}</span></td>
                            <td>{inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-IN') : '-'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.4rem' }}>
                                {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                                  <button className="btn btn-success btn-sm" onClick={() => openPaymentModal(inv)}>
                                    <HiCurrencyRupee /> Pay
                                  </button>
                                )}
                                <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(inv)}><HiTrash /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && total > limit && (
              <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)' }}>
                <div className="pagination">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'payments' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Payment Records</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Invoice Amount Rs.</th>
                  <th>Paid Rs.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No payment records.</td></tr>
                ) : (
                  payments.map((inv) => (
                    <tr key={inv.id || inv._id}>
                      <td style={{ fontWeight: 600 }}>{inv.invoice_number || `#${inv.id || inv._id}`}</td>
                      <td>{inv.customer_name || inv.customer?.name || `ID: ${inv.customer_id}`}</td>
                      <td>Rs.{fmt(inv.amount)}</td>
                      <td>Rs.{fmt(inv.paid_amount)}</td>
                      <td><span className={`badge ${STATUS_CLASS[inv.status] || 'badge-gray'}`}>{inv.status}</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Invoice Modal */}
      {newInvoiceModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setNewInvoiceModal(false)}>
          <div className="modal" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <span className="modal-title">New Invoice</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setNewInvoiceModal(false)}><HiXMark /></button>
            </div>
            <form onSubmit={handleCreateInvoice}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Customer ID *</label>
                  <input className="form-control" type="number" name="customer_id" value={invoiceForm.customer_id} onChange={handleInvoiceFormChange} min="1" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount (Rs.) *</label>
                  <input className="form-control" type="number" name="amount" value={invoiceForm.amount} onChange={handleInvoiceFormChange} min="0" step="0.01" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-control" type="date" name="due_date" value={invoiceForm.due_date} onChange={handleInvoiceFormChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" name="notes" value={invoiceForm.notes} onChange={handleInvoiceFormChange} rows={2} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setNewInvoiceModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingInvoice}>
                  {savingInvoice ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {paymentModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setPaymentModal(null)}>
          <div className="modal" style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <span className="modal-title">Record Payment</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setPaymentModal(null)}><HiXMark /></button>
            </div>
            <form onSubmit={handleRecordPayment}>
              <div className="modal-body">
                <p className="text-muted" style={{ marginBottom: '1rem' }}>
                  Invoice <strong>{paymentModal.invoice_number || `#${paymentModal.id || paymentModal._id}`}</strong> - Balance: Rs.{fmt(Number(paymentModal.balance) || Number(paymentModal.amount) - Number(paymentModal.paid_amount || 0))}
                </p>
                <div className="form-group">
                  <label className="form-label">Amount (Rs.) *</label>
                  <input className="form-control" type="number" name="amount" value={paymentForm.amount} onChange={handlePaymentFormChange} min="0.01" step="0.01" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-control" name="method" value={paymentForm.method} onChange={handlePaymentFormChange}>
                    {['cash', 'upi', 'bank_transfer', 'cheque', 'card'].map((m) => (
                      <option key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Transaction Reference</label>
                  <input className="form-control" name="transaction_ref" value={paymentForm.transaction_ref} onChange={handlePaymentFormChange} placeholder="UTR, cheque no., etc." />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" name="notes" value={paymentForm.notes} onChange={handlePaymentFormChange} rows={2} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPaymentModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={savingPayment}>
                  {savingPayment ? 'Saving...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <span className="modal-title">Delete Invoice</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <p>Delete invoice <strong>{deleteTarget.invoice_number || `#${deleteTarget.id || deleteTarget._id}`}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
